import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled, { ThemeProvider } from 'styled-components';
import { Head, Loader, Nav, Social, Email, Footer } from '@components';
import { GlobalStyle, theme } from '@styles';

const StyledContent = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

const Layout = ({ children, location }) => {
  const isHome = location.pathname === '/';
  // Start with loader on the home page so the SVG is present on first paint; refine after mount.
  const [isLoading, setIsLoading] = useState(isHome);

  // Decide if the loader should remain after hydration (client-only; avoids SSR window issues).
  useEffect(() => {
    if (!isHome || typeof window === 'undefined') {
      setIsLoading(false);
      return;
    }

    const hasLoaded = sessionStorage.getItem('hasLoaded') === 'true';
    const showLoader = sessionStorage.getItem('showLoader') === 'true';
    const shouldShowLoader = showLoader || !hasLoaded;

    setIsLoading(shouldShowLoader);
    sessionStorage.setItem('hasLoaded', 'true');
  }, [isHome]);

  // Sets target="_blank" rel="noopener noreferrer" on external links
  const handleExternalLinks = () => {
    const allLinks = Array.from(document.querySelectorAll('a'));
    if (allLinks.length > 0) {
      allLinks.forEach(link => {
        if (link.host !== window.location.host) {
          link.setAttribute('rel', 'noopener noreferrer');
          link.setAttribute('target', '_blank');
        }
      });
    }
  };

  useEffect(() => {
    if (isLoading) {
      return;
    }

    // Handle hash navigation
    const handleHashNavigation = () => {
      if (location.hash) {
        const id = location.hash.substring(1); // location.hash without the '#'
        // Use a longer timeout to ensure DOM is ready
        setTimeout(() => {
          const el = document.getElementById(id);
          if (el) {
            // Account for fixed header height
            const headerOffset = 100;
            const elementPosition = el.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth',
            });
            el.focus();
          }
        }, 100);
      }
    };

    handleHashNavigation();
    handleExternalLinks();
  }, [isLoading, location.hash]); // Add location.hash to dependencies

  return (
    <>
      <Head />

      <div id="root">
        <ThemeProvider theme={theme}>
          <GlobalStyle />

          <a className="skip-to-content" href="#content">
            Skip to Content
          </a>

          {isLoading && isHome ? (
            <Loader
              finishLoading={() => {
                setIsLoading(false);
                if (typeof window !== 'undefined') {
                  sessionStorage.removeItem('showLoader');
                }
              }}
            />
          ) : (
            <StyledContent>
              <Nav isHome={isHome} />
              <Social isHome={isHome} />
              <Email isHome={isHome} />

              <div id="content">
                {children}
                <Footer />
              </div>
            </StyledContent>
          )}
        </ThemeProvider>
      </div>
    </>
  );
};

Layout.propTypes = {
  children: PropTypes.node.isRequired,
  location: PropTypes.object.isRequired,
};

export default Layout;
